import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { TGetEncryptedQueryResponse } from '../types/products';
import type { RedirectResponse, RenderStyleResponse } from '../types/redirect';
import type {
  CheckDocumentExistenceResponse,
  CheckDraftReportExistsResponse,
  ReportApiResponse,
  ReportDetailsResponse,
  ReportFilterApiResponse,
} from '../types/report';
import type { ExportReportExcelResponse } from '../types/singleReport';
import { getLoginUserOrgType } from '../util';
import { encodeXmlStringToBase64 } from '../util/reportUtils';
import { FileExportClient,HttpClient } from './axiosConfig';

export const useReportListData = (
  filters: {
    jurisdictionId?: string;
    source?: string;
    tagId?: string;
    analysisId?: string;
    agreementId?: string;
    docAuthor?: string;
    docAddressee?: string;
    counterPartyId: string;
    isSupportingGrid?: string;
    sortBy?: string;
    sortOrder?: string;
    categoryId?: string;
    categoryType?: string;
    hideFromUsers?: number;
    isDraft?: number;
    isBookmarkedPage?: string;
    isRecentViewedPage?: string;
    isRecentUpdatesPage?: string;
    isArchivedPage?: string;
    pendingSourceFromUrl?: string;
  },
  numberOfRecords = 50,
  currentPage = 1
) => {
  const shouldWaitForSource = filters.pendingSourceFromUrl && 
    filters.pendingSourceFromUrl !== '-1' && 
    filters.source === '-1';
  
  return useQuery({
    queryKey: [
      'getFindReports',
      numberOfRecords,
      currentPage,
      filters.jurisdictionId,
      filters.source,
      filters.tagId,
      filters.isSupportingGrid,
      filters.sortBy,
      filters.sortOrder,
      filters.categoryId,
      filters.categoryType,
      filters.hideFromUsers,
      filters.isDraft,
      filters.analysisId,
      filters.agreementId,
      filters.docAuthor,
      filters.docAddressee,
      filters.counterPartyId,
      filters.isBookmarkedPage,
      filters.isRecentViewedPage,
      filters.isRecentUpdatesPage,
      filters.isArchivedPage,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<ReportApiResponse>(
        `users/reports?jurisdictionId=${filters.jurisdictionId}&source=${filters.source}&tagId=${filters.tagId}&isDraft=${filters.isDraft}&sortBy=${filters.sortBy ?? 'desc'}&orderBy=${filters.sortOrder ?? (filters.isRecentViewedPage ? 'dateaccessed' : 'lastModified')}&categoryType=${filters.categoryType}&categoryId=${filters.categoryId}&pageNumber=${currentPage}&numberOfRecords=${numberOfRecords}&analysisId=${filters.analysisId}&counterPartyId=${filters.counterPartyId}&agreementId=${filters.agreementId}&docAuthor=${filters.docAuthor}&docAddressee=${filters.docAddressee}${
          filters.isBookmarkedPage
            ? `&isBookmarkedPage=${filters.isBookmarkedPage}`
            : ''
        }${
          filters.isRecentViewedPage
            ? `&isRecentViewedPage=${filters.isRecentViewedPage}`
            : ''
        }${
          filters.isRecentUpdatesPage
            ? `&modifiedDate=${filters.isRecentUpdatesPage}`
            : ''
        }${
          filters.isArchivedPage
            ? `&isArchivedPage=${filters.isArchivedPage}`
            : ''
        }`
      );
      return result.data;
    },
    enabled: filters?.categoryId !== '0' && filters.categoryType !== null && !shouldWaitForSource,
  });
};
export const useReportFilterListData = (
  filters: {
    jurisdictionId?: string;
    source?: string;
    tagId?: string;
    analysisId?: string;
    agreementId?: string;
    docAuthor?: string;
    docAddressee?: string;
    counterPartyId: string;
    isSupportingGrid?: string;
    sortBy?: string;
    sortOrder?: string;
    categoryId?: string;
    categoryType?: string;
    hideFromUsers?: number;
    isDraft?: number;
    isBookmarkedPage?: string;
    isRecentViewedPage?: string;
    isRecentUpdatesPage?: string;
    isArchivedPage?: string;
    pendingSourceFromUrl?: string;
  },
  numberOfRecords = 50,
  currentPage = 1
) => {
  const shouldWaitForSource = filters.pendingSourceFromUrl && 
    filters.pendingSourceFromUrl !== '-1' && 
    filters.source === '-1';
  
  return useQuery({
    queryKey: [
      'getFindReportsFilters',
      numberOfRecords,
      currentPage,
      filters.jurisdictionId,
      filters.source,
      filters.tagId,
      filters.isSupportingGrid,
      filters.sortBy,
      filters.sortOrder,
      filters.categoryId,
      filters.categoryType,
      filters.hideFromUsers,
      filters.isDraft,
      filters.analysisId,
      filters.agreementId,
      filters.docAuthor,
      filters.docAddressee,
      filters.counterPartyId,
      filters.isBookmarkedPage,
      filters.isRecentViewedPage,
      filters.isRecentUpdatesPage,
      filters.isArchivedPage,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<ReportFilterApiResponse>(
        `users/reports/filters?jurisdictionId=${filters.jurisdictionId}&source=${filters.source}&tagId=${filters.tagId}&isDraft=${filters.isDraft}&sortBy=${filters.sortBy ?? 'desc'}&orderBy=${filters.sortOrder ?? (filters.isRecentViewedPage ? 'dateaccessed' : 'lastModified')}&categoryType=${filters.categoryType}&categoryId=${filters.categoryId}&pageNumber=${currentPage}&numberOfRecords=${numberOfRecords}&analysisId=${filters.analysisId}&counterPartyId=${filters.counterPartyId}&agreementId=${filters.agreementId}&docAuthor=${filters.docAuthor}&docAddressee=${filters.docAddressee}${
          filters.isBookmarkedPage
            ? `&isBookmarkedPage=${filters.isBookmarkedPage}`
            : ''
        }${
          filters.isRecentViewedPage
            ? `&isRecentViewedPage=${filters.isRecentViewedPage}`
            : ''
        }${
          filters.isRecentUpdatesPage
            ? `&modifiedDate=${filters.isRecentUpdatesPage}`
            : ''
        }${
          filters.isArchivedPage
            ? `&isArchivedPage=${filters.isArchivedPage}`
            : ''
        }`
      );
      return result.data;
    },
    enabled: filters?.categoryId !== '0' && filters.categoryType !== null && !shouldWaitForSource,
  });
};

export const getReportBasicDetails = async (
  reportId: number
): Promise<ReportDetailsResponse> => {
  const result = await HttpClient.get<ReportDetailsResponse>(
    `users/reports/${reportId}/basic-details`
  );
  return result.data;
};

export const useViewReportDetails = (
  reportId: number,
  options?: { enabled: boolean }
) => {
  return useQuery({
    queryKey: ['reportDetails', reportId],
    queryFn: async () => getReportBasicDetails(reportId),
    enabled: options?.enabled ?? true,
  });
};

export const bookmarkReportAPI = async ({
  opinionId,
  isBookmarked,
}: {
  opinionId: number;
  isBookmarked: boolean;
}) => {
  return HttpClient.post(
    `users/reports/${opinionId}/bookmarks?isBookmark=${isBookmarked}`
  );
};

/** Redirect by encrypted URL only (e.g. from shared link). */
type RedirectByEurl = {
  eurl: string;
  reportId?: number;
  renderStyle?: number;
  pageType?: string;
  entityCheck?: number;
  version?: number;
  preview?: number;
  keyValue?: string;
};

/** Redirect by report id and render style (full params). */
type RedirectByParameters = {
  reportId: number;
  renderStyle: number;
  pageType?: string;
  entityCheck?: number;
  version?: number;
  preview?: number;
  eurl?: string;
  keyValue?: string;
};

/** Either eurl or (reportId + renderStyle) is required. */
export type RedirectParameters = RedirectByEurl | RedirectByParameters;

export const fetchRedirectData = async ({
  reportId,
  renderStyle,
  pageType = 'home',
  entityCheck = 1,
  preview,
  eurl,
  keyValue,
}: RedirectParameters) => {
  const userOrgType = getLoginUserOrgType();
  const query = new URLSearchParams();

  if (eurl && (!reportId || !renderStyle)) {
    // eUrl-only redirect (no reportId/renderStyle)
    query.append('eUrl', eurl);
    if (keyValue) {
      query.append('keyValue', keyValue);
    }
    if (preview !== undefined) {
      query.append('preview', String(preview));
    }
  } else if (reportId && renderStyle) {
    // Full parameter-based redirect
    query.append('reportId', String(reportId));
    query.append('renderStyle', String(renderStyle));
    query.append('pageType', pageType);
    query.append('entityCheck', String(entityCheck));
    if (keyValue) {
      query.append('keyValue', keyValue);
    }
    if (eurl) {
      query.append('eurl', eurl);
    }
    // Only add preview parameter if it's provided
    if (preview !== undefined) {
      query.append('preview', String(preview));
    }
  } else {
    throw new Error(
      'Invalid redirect parameters: provide either (reportId & renderStyle) or eUrl',
    );
  }

  const response = await HttpClient.get<RedirectResponse>(
    `/users/reports/redirect?${query.toString()}`,
    {
      headers: {
        loginUserOrgType: userOrgType,
      },
    }
  );

  return response.data;
};
export const useRedirectReport = () => {
  return useMutation({
    mutationFn: (parameters: RedirectParameters) =>
      fetchRedirectData(parameters),
  });
};

export const useRedirectForCompareReport = async (payload: {
  reportIds: Array<string>;
  renderingTypeId: number;
  productId: string;
  reportId: string;
  groupByRegions: number;
  pageType: string;
  keyValue: string;
  secReportId?: string | number;
  secFactIds?: Array<string>;
}) => {
  const formData = new FormData();
  formData.append('reportIds', payload.reportIds.join(','));
  formData.append('renderingTypeId', String(payload.renderingTypeId));
  formData.append('productId', String(payload.productId));
  formData.append('reportId', String(payload.reportId));
  formData.append('groupByRegions', String(payload.groupByRegions));
  formData.append('pageType', payload.pageType);
  formData.append('keyValue', payload.keyValue);
  if (payload.secReportId !== undefined && payload.secReportId !== null) {
    formData.append('secReportId', String(payload.secReportId));
  }
  if (payload.secFactIds && payload.secFactIds.length > 0) {
    formData.append('secFactIds', payload.secFactIds.join(',')); // Send as comma-separated string
  }
  const response = await HttpClient.post(
    `users/reports-compare/redirect`,
    formData
  );
  return response.data;
};
export const useCheckDraftReportExistsMutation = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const response = await HttpClient.get<CheckDraftReportExistsResponse>(
        `users/reports/check-draft-report-exists?reportId=${id}`
      );
      return response.data;
    },
  });

export const useCheckDocumentExistenceMutation = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const response = await HttpClient.get<CheckDocumentExistenceResponse>(
        `users/reports/check-document-existence?reportId=${id}`
      );
      return response.data;
    },
  });

export const useRedirectReportMutation = () =>
  useMutation({
    mutationFn: async (eventURL: string) => {
      const response = await HttpClient.post<RedirectResponse>(
        `users/reports/edit/redirect?eUrl=${encodeURIComponent(eventURL)}`
      );
      return response.data;
    },
  });

export const useArchivedRedirectReportMutation = () =>
  useMutation({
    mutationFn: async (eventURL: string) => {
      const response = await HttpClient.post<RedirectResponse>(
        `users/reports/archived/process?eUrl=${encodeURIComponent(eventURL)}`
      );
      return response.data;
    },
  });
export const useDraftRedirectReportMutation = () =>
  useMutation({
    mutationFn: async (eventURL: string) => {
      const response = await HttpClient.post<RedirectResponse>(
        `users/reports/draft/process?eUrl=${encodeURIComponent(eventURL)}`
      );
      return response.data;
    },
  });

export const useEncryptMutation = () => {
  return useMutation({
    mutationFn: async (input: string) => {
      const response = await HttpClient.get<TGetEncryptedQueryResponse>(
        `commons/encrypt?input=${encodeURIComponent(input)}`
      );
      return response.data;
    },
  });
};
export const getProductCategoryDetails = async (
  categoryId: string | number
): Promise<AxiosResponse<RenderStyleResponse>> => {
  return HttpClient.get<RenderStyleResponse>('users/products/categories', {
    params: { categoryId },
  });
};

export const useGetProductCategoryDetails = (
  categoryId: string,
  options?: { refetchOnMount?: boolean | 'always' },
) => {
  return useQuery({
    queryKey: ['categoryDetails', categoryId],
    queryFn: async () => {
      const result = await getProductCategoryDetails(categoryId);
      return result.data;
    },
    enabled: !!categoryId,
    refetchOnMount: options?.refetchOnMount ?? false,
  });
};

// Guest User Reports
export const useGuestUserReportListData = (
  filters: {
    jurisdictionId?: string;
    source?: string;
    analysisId?: string;
    docAuthor?: string;
    sortBy?: string;
    sortOrder?: string;
    pendingSourceFromUrl?: string;
  },
  numberOfRecords = 50,
  currentPage = 1
) => {
  const shouldWaitForSource = filters.pendingSourceFromUrl && 
    filters.pendingSourceFromUrl !== '-1' && 
    filters.source === '-1';
  
  return useQuery({
    queryKey: [
      'getGuestUserReports',
      numberOfRecords,
      currentPage,
      filters.jurisdictionId,
      filters.source,
      filters.analysisId,
      filters.docAuthor,
      filters.sortBy,
      filters.sortOrder,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<ReportApiResponse>(
        `guest-users/reports?jurisdictionId=${filters.jurisdictionId}&source=${filters.source}&sortBy=${filters.sortBy ?? 'desc'}&orderBy=${filters.sortOrder ?? 'lastModified'}&pageNumber=${currentPage}&numberOfRecords=${numberOfRecords}&analysisId=${filters.analysisId}&docAuthor=${filters.docAuthor}`
      );
      return result.data;
    },
    enabled: !shouldWaitForSource,
  });
};

export const useGuestUserReportFilterListData = (
  filters: {
    jurisdictionId?: string;
    source?: string;
    analysisId?: string;
    docAuthor?: string;
    sortBy?: string;
    sortOrder?: string;
    pendingSourceFromUrl?: string;
  },
  numberOfRecords = 50,
  currentPage = 1
) => {
  const shouldWaitForSource = filters.pendingSourceFromUrl && 
    filters.pendingSourceFromUrl !== '-1' && 
    filters.source === '-1';
  
  return useQuery({
    queryKey: [
      'getGuestUserReportsFilters',
      numberOfRecords,
      currentPage,
      filters.jurisdictionId,
      filters.source,
      filters.analysisId,
      filters.docAuthor,
      filters.sortBy,
      filters.sortOrder,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<ReportFilterApiResponse>(
        `guest-users/reports/filters?jurisdictionId=${filters.jurisdictionId}&source=${filters.source}&sortBy=${filters.sortBy ?? 'desc'}&orderBy=${filters.sortOrder ?? 'lastModified'}&pageNumber=${currentPage}&numberOfRecords=${numberOfRecords}&analysisId=${filters.analysisId}&docAuthor=${filters.docAuthor}`
      );
      return result.data;
    },
    enabled: !shouldWaitForSource,
  });
};

export interface GeneratePdfPayload {
  htmlContent: string;
  reportName?: string;
  pageLabel?: string;
  reportId?: string | number;
}

export interface GeneratePdfResponse {
  message?: string;
  data?: {
    fileUrl?: string;
    fileName?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}
export const usePrintReportMutation = () => {
  return useMutation({
    mutationFn: async (payload: GeneratePdfPayload) => {
      const reportId = payload.reportId;
      const base64HtmlContent = encodeXmlStringToBase64(payload.htmlContent);
      const response = await FileExportClient.post<GeneratePdfResponse | Blob>(
        `users/reports/version/pdf?reportId=${reportId}`,
        {
          source: base64HtmlContent,
        },
        {
          responseType: 'blob', 
        }
      );
      return response.data;
    },
  });
};

export const exportReportsToExcel = async (filters: {
  jurisdictionId?: string;
  source?: string;
  tagId?: string;
  analysisId?: string;
  agreementId?: string;
  docAuthor?: string;
  docAddressee?: string;
  counterPartyId?: string;
  sortBy?: string;
  sortOrder?: string;
  categoryId?: string;
  categoryType?: string;
  isDraft?: number;
}): Promise<ExportReportExcelResponse> => {
  const parameters = new URLSearchParams();
  parameters.append('jurisdictionId', filters.jurisdictionId ?? '-1');
  parameters.append('source', filters.source ?? '-1');
  parameters.append('tagId', filters.tagId ?? '-1');
  parameters.append('isDraft', String(filters.isDraft ?? 1));
  parameters.append('sortBy', filters.sortBy ?? 'desc');
  parameters.append('orderBy', filters.sortOrder ?? 'lastModified');
  parameters.append('categoryId', filters.categoryId ?? '0');
  parameters.append('categoryType', filters.categoryType ?? '0');
  parameters.append('pageNumber', '1');
  parameters.append('numberOfRecords', '-1');
  parameters.append('analysisId', filters.analysisId ?? '-1');
  parameters.append('counterPartyId', filters.counterPartyId ?? '-1');
  parameters.append('agreementId', filters.agreementId ?? '-1');
  parameters.append('docAuthor', filters.docAuthor ?? '-1');
  parameters.append('docAddressee', filters.docAddressee ?? '-1');
  const response = await FileExportClient.get<ExportReportExcelResponse>(
    `users/reports/excel?${parameters.toString()}`,
  );

  return response?.data;
};

export const useExportReportsToExcelMutation = () => {
  return useMutation({
    mutationFn: exportReportsToExcel,
  });
};